import React from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  ListSubheader,
  FormControl,
  Autocomplete,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ButtonOne from "../../../common/normal-button";
import { ColorsConstants, borderColors } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import {
  ENTRY_TYPE_GROUPS,
  MATCH_MODE_OPTIONS,
  MAX_TRIGGERS,
  isCatchAllRow,
  selectFieldSx,
  textFieldSx,
  labelSx,
} from "../utils";

// Shared trigger-rows editor UI — identical between WorkflowEdit and CustomChatbotEdit.
// The trigger-type select (grouped via ENTRY_TYPE_GROUPS), the per-type row body (text
// keyword vs template button reply), and the add-row/counter/error footer. Callers own
// the surrounding margin/label wrapper and all the state (triggers/templateOptions/
// etc) — this just renders it and calls back on add/remove/update.
const TriggerRowsEditor = ({
  triggers,
  templateOptions,
  templatesLoading,
  errorMessage,
  onAddRow,
  onRemoveRow,
  onUpdateRow,
}) => (
  <>
    <Typography sx={labelSx}>Triggers</Typography>

    {triggers.map((row) => (
      <Box key={row.key} sx={{ border: "1px solid #E5E7EB", borderRadius: "8px", p: 2, mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <Select
              value={row.type}
              onChange={(e) =>
                onUpdateRow(row.key, {
                  type: e.target.value,
                  value: "",
                  matchMode: "exact",
                  templateMode: "specific",
                  selectedTemplate: null,
                  selectedButton: null,
                })
              }
              sx={selectFieldSx}
            >
              {ENTRY_TYPE_GROUPS.flatMap((group) => [
                <ListSubheader key={`group-${group.label}`} sx={{ ...typography.bodyLarge, fontWeight: 700 }}>
                  {group.label}
                </ListSubheader>,
                ...group.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled} sx={{ ...typography.bodyLarge, pl: 3 }}>
                    {opt.label}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={() => onRemoveRow(row.key)}
            sx={{
              color: "#0F172A",
              borderRadius: "6px",
              "&:hover": { bgcolor: "#FFF0F0", color: "#E05252" },
            }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {row.type === "text" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={row.matchMode === "any" ? "any" : "specific"}
                onChange={(e) =>
                  onUpdateRow(
                    row.key,
                    e.target.value === "any"
                      ? { matchMode: "any", value: "" }
                      : { matchMode: "exact", value: "" }
                  )
                }
                sx={selectFieldSx}
              >
                <MenuItem value="specific" sx={{ ...typography.bodyLarge }}>Specific Keywords</MenuItem>
                <MenuItem value="any" sx={{ ...typography.bodyLarge }}>Any Text</MenuItem>
              </Select>
            </FormControl>

            {row.matchMode === "any" ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "8px",
                  backgroundColor: "#F0F5FF",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, color: ColorsConstants.primaryBlue100, mt: "1px" }} />
                <Typography sx={{ ...typography.bodySmall, color: "#3D4A5C" }}>
                  This bot responds to any incoming text message not caught by a more
                  specific trigger on another bot — no keyword needed. It can't be
                  combined with other triggers on this bot, and only one bot across the
                  whole tenant can use it at a time.
                </Typography>
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  placeholder="e.g. hello"
                  value={row.value}
                  onChange={(e) => onUpdateRow(row.key, { value: e.target.value })}
                  sx={textFieldSx}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={row.matchMode}
                    onChange={(e) => onUpdateRow(row.key, { matchMode: e.target.value })}
                    sx={selectFieldSx}
                  >
                    {MATCH_MODE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled} sx={{ ...typography.bodyLarge }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        ) : row.type === "any_message" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: 1.5,
              py: 1.25,
              borderRadius: "8px",
              backgroundColor: "#F0F5FF",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: ColorsConstants.primaryBlue100, mt: "1px" }} />
            <Typography sx={{ ...typography.bodySmall, color: "#3D4A5C" }}>
              This bot responds to any incoming message not caught by a more specific
              trigger on another bot — media, stickers, locations, contacts, button
              taps, anything, not just text (for text-only, use "Any Text" under Text
              Keyword instead). No keyword needed. It can't be combined with other
              triggers on this bot, and only one bot across the whole tenant can use it
              at a time.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={row.templateMode === "any" ? "any" : "specific"}
                onChange={(e) =>
                  onUpdateRow(
                    row.key,
                    e.target.value === "any"
                      ? { templateMode: "any", value: "", selectedTemplate: null, selectedButton: null }
                      : { templateMode: "specific", value: "" }
                  )
                }
                sx={selectFieldSx}
              >
                <MenuItem value="specific" sx={{ ...typography.bodyLarge }}>Specific Template</MenuItem>
                <MenuItem value="any" sx={{ ...typography.bodyLarge }}>Any Template</MenuItem>
              </Select>
            </FormControl>

            {row.templateMode === "any" ? (
              <>
                <TextField
                  fullWidth
                  placeholder="e.g. Yes, I'm interested"
                  value={row.value}
                  onChange={(e) => onUpdateRow(row.key, { value: e.target.value })}
                  sx={textFieldSx}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={row.matchMode}
                    onChange={(e) => onUpdateRow(row.key, { matchMode: e.target.value })}
                    sx={selectFieldSx}
                  >
                    {MATCH_MODE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled} sx={{ ...typography.bodyLarge }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "8px",
                    backgroundColor: "#F0F5FF",
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: ColorsConstants.primaryBlue100, mt: "1px" }} />
                  <Typography sx={{ ...typography.bodySmall, color: "#3D4A5C" }}>
                    Triggers when a customer taps a button on ANY approved template —
                    not just one specific template — whose text matches this value
                    according to the match mode selected above.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={templateOptions}
                  value={row.selectedTemplate}
                  loading={templatesLoading}
                  onChange={(e, newValue) =>
                    onUpdateRow(row.key, { selectedTemplate: newValue, selectedButton: null })
                  }
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="No approved templates found"
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select an approved template" sx={textFieldSx} />
                  )}
                />
                <Autocomplete
                  size="small"
                  fullWidth
                  options={row.selectedTemplate?.buttons || []}
                  value={row.selectedButton}
                  disabled={!row.selectedTemplate}
                  onChange={(e, newValue) => onUpdateRow(row.key, { selectedButton: newValue })}
                  getOptionLabel={(option) => option.text}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText="Selected template has no buttons"
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select a button" sx={textFieldSx} />
                  )}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    ))}

    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
      <ButtonOne
        variant="outlined"
        onClick={onAddRow}
        disabled={triggers.length >= MAX_TRIGGERS || triggers.some(isCatchAllRow)}
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        sx={{
          height: 36,
          px: 2,
          ...typography.bodyLarge,
          border: `1px solid ${borderColors.inputFieldGrey}`,
          color: "#1D2330",
          "&:hover": { border: `1px solid ${ColorsConstants.primaryGray50}`, backgroundColor: "transparent" },
        }}
      >
        Add Trigger
      </ButtonOne>
      <Typography sx={{ ...typography.bodySmall, color: "#9CA3AF" }}>
        {triggers.length} / {MAX_TRIGGERS} triggers
      </Typography>
    </Box>
    {errorMessage && (
      <Typography sx={{ ...typography.bodySmall, color: "#E05252", mt: 0.5 }}>
        {errorMessage}
      </Typography>
    )}
  </>
);

export default TriggerRowsEditor;
